import { Alert, AlertSection, AlertType, isDefined, isUndefined } from '@aloft/toolkit-shared';

import { useMemo } from 'react';

import { selectCompAdjustments } from '../../../../redux/features/selectors/adjustmentSelectors.ts';
import { selectAlerts } from '../../../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import {
  selectCompSequence,
  selectOrder,
} from '../../../../redux/features/selectors/orderSelectors.ts';
import { selectPriorSales } from '../../../../redux/features/selectors/priorSalesSelectors.ts';
import { selectPropertyDataEntities } from '../../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectImprovementsCosts } from '../../../../redux/features/selectors/selectImprovementsCost.ts';
import { selectSiteValues } from '../../../../redux/features/selectors/selectSiteValue.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { allCompsAdjustedDown } from '../../../alerts/definitions/adjustments/allCompsAdjustedDown.ts';
import { allCompsAdjustedUp } from '../../../alerts/definitions/adjustments/allCompsAdjustedUp.ts';
import { bracketedMarketValue } from '../../../alerts/definitions/adjustments/bracketedMarketValue.ts';
import { limitedAdjustedSalePriceRange } from '../../../alerts/definitions/adjustments/limitedAdjustedSalePriceRange.ts';
import { bracketedGLA } from '../../../alerts/definitions/comparable/bracketedGLA.ts';
import { bracketedSiteSize } from '../../../alerts/definitions/comparable/bracketedSiteSize.ts';
import { lessThanThreeSettledSales } from '../../../alerts/definitions/comparable/lessThanThreeSettledSales.ts';
import { similarComparableToSubjectPriorSale } from '../../../alerts/definitions/comparable/similarComparableToSubjectPriorSale.ts';
import { bracketedSiteToValueRatio } from '../../../alerts/definitions/depreciatedCost/bracketedSiteToValueRatio.ts';
import { effectiveAgeLowerThanActualAge } from '../../../alerts/definitions/depreciatedCost/effectiveAgeLowerThanActualAge.ts';
import { inconsistentCompGlaAdjustment } from '../../../alerts/definitions/depreciatedCost/inconsistentCompGlaAdjustment.ts';
import { inconsistentCompSiteAdjustment } from '../../../alerts/definitions/depreciatedCost/inconsistentCompSiteAdjustment.ts';
import { largestSiteHasHighestValue } from '../../../alerts/definitions/depreciatedCost/largestSiteHasHighestValue.ts';
import { limitedSiteToValueRatioRange } from '../../../alerts/definitions/depreciatedCost/limitedSiteToValueRatioRange.ts';
import { negativeSiteValue } from '../../../alerts/definitions/depreciatedCost/negativeSiteValue.ts';
import { smallestSiteHasLowestValue } from '../../../alerts/definitions/depreciatedCost/smallestSiteHasLowestValue.ts';
import { AlertDefinition, AlertDefinitionParams } from '../../../alerts/types.ts';

const buildAlertSection = (
  alertDefinitions: AlertDefinition[],
  params: AlertDefinitionParams,
  dismissedAlerts: AlertType[],
): Alert[] =>
  alertDefinitions
    .map((alertDefinition) => alertDefinition(params))
    .filter(isDefined)
    .map((alert) => ({
      ...alert,
      status: dismissedAlerts.includes(alert.type) ? 'dismissed' : 'active',
    }));

export const useAlerts = (): Record<AlertSection, Alert[]> => {
  const { dismissedAlerts } = useAppSelector(selectAlerts);
  const compAdjustments = useAppSelector(selectCompAdjustments);
  const compSequence = useAppSelector(selectCompSequence);
  const improvementCosts = useAppSelector(selectImprovementsCosts);
  const order = useAppSelector(selectOrder);
  const priorSales = useAppSelector(selectPriorSales);
  const propertyDataEntities = useAppSelector(selectPropertyDataEntities);
  const siteValues = useAppSelector(selectSiteValues);

  if (
    [
      dismissedAlerts,
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
    ].some(isUndefined)
  ) {
    return {
      adjustments: [],
      comparable: [],
      depreciatedCost: [],
    };
  }

  const alertDefinitionParams: AlertDefinitionParams = useMemo(
    () => ({
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
    }),
    [
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
    ],
  );

  const adjustments: Alert[] = useMemo(
    () =>
      buildAlertSection(
        [
          limitedAdjustedSalePriceRange,
          allCompsAdjustedDown,
          allCompsAdjustedUp,
          bracketedMarketValue,
        ],
        alertDefinitionParams,
        dismissedAlerts,
      ),
    [alertDefinitionParams, dismissedAlerts],
  );

  const comparable: Alert[] = useMemo(
    () =>
      buildAlertSection(
        [
          lessThanThreeSettledSales,
          bracketedGLA,
          bracketedSiteSize,
          similarComparableToSubjectPriorSale,
        ],
        alertDefinitionParams,
        dismissedAlerts,
      ),
    [alertDefinitionParams, dismissedAlerts],
  );

  const depreciatedCost: Alert[] = useMemo(
    () =>
      buildAlertSection(
        [
          effectiveAgeLowerThanActualAge,
          negativeSiteValue,
          smallestSiteHasLowestValue,
          largestSiteHasHighestValue,
          bracketedSiteToValueRatio,
          limitedSiteToValueRatioRange,
          inconsistentCompSiteAdjustment,
          inconsistentCompGlaAdjustment,
        ],
        alertDefinitionParams,
        dismissedAlerts,
      ),
    [alertDefinitionParams, dismissedAlerts],
  );

  return {
    adjustments,
    comparable,
    depreciatedCost,
  };
};
